/**
 * Welke bestanden een foto zijn, en of dit apparaat ze kan lezen.
 *
 * Een inspecteur op een iPad kiest of sleept foto's uit de Foto's-app; die zijn
 * vaak HEIC. De server bepaalt het type op de inhoud en zet HEIC om, dus het
 * apparaat hoeft alleen het bestand door te laten - al maakt `shrinkPhoto` er
 * een JPEG van als de browser hem kan lezen.
 *
 * Twee dingen gaan op een telefoon echt mis, en allebei zag je hier niet:
 *
 * - De browser kent niet altijd een MIME-type (Chrome op een Mac geeft voor
 *   HEIC een lege string). Daarom telt de extensie mee, én - net als op de
 *   server - de eerste bytes van het bestand zelf.
 * - Een foto die alleen in iCloud staat komt als leeg of onleesbaar bestand
 *   binnen. Die moet je zien; stil overslaan lijkt op "het werkt af en toe
 *   niet".
 */

const EXTENSION_TYPES: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
};

/** Voor `<input accept>`: alle afbeeldingen, en HEIC/HEIF ook op extensie. */
export const PHOTO_ACCEPT = 'image/*,image/heic,image/heif,.heic,.heif';

/**
 * Zoveel bytes van het begin zijn genoeg om het type aan de inhoud te herkennen:
 * het merk van een HEIC staat op byte 8 tot en met 11.
 */
const PROBE_BYTES = 16;

/** De merken waarmee een HEIF-container een foto draagt; `mp42` en `isom` zijn video. */
const HEIF_BRANDS: Record<string, string> = {
    heic: 'image/heic',
    heix: 'image/heic',
    heim: 'image/heic',
    heis: 'image/heic',
    hevc: 'image/heic',
    hevm: 'image/heic',
    hevs: 'image/heic',
    heif: 'image/heif',
    mif1: 'image/heif',
    msf1: 'image/heif',
};

function extensionOf(name: string): string {
    const dot = name.lastIndexOf('.');

    return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

export function isPhotoFile(file: Pick<File, 'name' | 'type'>): boolean {
    return file.type.startsWith('image/') || extensionOf(file.name) in EXTENSION_TYPES;
}

function startsWith(head: Uint8Array, bytes: readonly number[]): boolean {
    return bytes.every((byte, index) => head[index] === byte);
}

function ascii(head: Uint8Array, from: number, to: number): string {
    return String.fromCharCode(...head.subarray(from, to));
}

/**
 * Het type volgens de inhoud, net als de server het bepaalt: een naam liegt,
 * bytes niet. `null` als deze eerste bytes geen foto zijn die wij versturen.
 */
export function sniffPhotoType(head: Uint8Array): string | null {
    if (startsWith(head, [0xff, 0xd8, 0xff])) {
        return 'image/jpeg';
    }

    if (startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        return 'image/png';
    }

    if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WEBP') {
        return 'image/webp';
    }

    if (ascii(head, 4, 8) === 'ftyp') {
        return HEIF_BRANDS[ascii(head, 8, 12)] ?? null;
    }

    return null;
}

/** Het MIME-type van een foto: eerst de inhoud, dan wat de browser zei, dan de extensie. */
export function photoMimeType(file: Pick<File, 'name' | 'type'>, head?: Uint8Array): string {
    const sniffed = head ? sniffPhotoType(head) : null;

    if (sniffed) {
        return sniffed;
    }

    if (file.type.startsWith('image/')) {
        return file.type;
    }

    return EXTENSION_TYPES[extensionOf(file.name)] ?? 'image/jpeg';
}

/** HEIC/HEIF: de server zet hem om, maar niet elke browser kan hem tonen. */
export function isHeic(mimeType: string): boolean {
    return mimeType === 'image/heic' || mimeType === 'image/heif';
}

/** Waarom een bestand niet als foto bij de vraag terechtkwam. */
export type PhotoRefusal = 'empty' | 'unreadable' | 'notAPhoto';

export interface RefusedPhoto {
    fileName: string;
    reason: PhotoRefusal;
}

export interface PhotoIntake {
    /** De bestanden die door mogen, in de volgorde waarin ze kwamen. */
    accepted: File[];
    refused: RefusedPhoto[];
}

/** Een bestand zonder naam heeft in een melding nog steeds een naam nodig. */
export function photoFileName(file: Pick<File, 'name'>): string {
    return file.name || 'foto.jpg';
}

/**
 * Bekijkt elk bestand apart: **één bestand dat niet meekan houdt de rest niet
 * tegen**. Dat is dezelfde regel als op de server (`UploadMediaBatch`), maar dan
 * vóór de upload - een selectie van twintig foto's waarvan er één in iCloud
 * staat, levert negentien foto's en één melding.
 *
 * Er worden maar `PROBE_BYTES` bytes gelezen. Dat is genoeg om het type te
 * herkennen én om te merken dat het bestand helemaal niet te lezen is, en het
 * kost geen geheugen bij een selectie van grote telefoonfoto's.
 */
export async function inspectPhotoFiles(files: readonly File[]): Promise<PhotoIntake> {
    const intake: PhotoIntake = { accepted: [], refused: [] };

    for (const file of files) {
        const reason = await refusalFor(file);

        if (reason) {
            intake.refused.push({ fileName: photoFileName(file), reason });
        } else {
            intake.accepted.push(file);
        }
    }

    return intake;
}

async function refusalFor(file: File): Promise<PhotoRefusal | null> {
    if (file.size === 0) {
        return 'empty';
    }

    let head: Uint8Array;

    try {
        head = new Uint8Array(await file.slice(0, PROBE_BYTES).arrayBuffer());
    } catch {
        return 'unreadable';
    }

    if (head.length === 0) {
        return 'unreadable';
    }

    return sniffPhotoType(head) === null && !isPhotoFile(file) ? 'notAPhoto' : null;
}

/**
 * De bestanden uit een drop, ongefilterd: wat geen foto is, krijgt zijn eigen
 * melding in plaats van stilte. `files` is er meestal; Safari op iPad levert bij
 * slepen uit de Foto's-app soms alleen `items`.
 */
export function droppedFiles(
    transfer: Pick<DataTransfer, 'files' | 'items'> | null | undefined,
): File[] {
    if (!transfer) {
        return [];
    }

    const fromFiles = Array.from(transfer.files ?? []);

    if (fromFiles.length > 0) {
        return fromFiles;
    }

    const fromItems: File[] = [];

    for (const item of Array.from(transfer.items ?? [])) {
        if (item.kind === 'file') {
            const file = item.getAsFile();

            if (file) {
                fromItems.push(file);
            }
        }
    }

    return fromItems;
}

/** Tijdens het slepen: zitten er bestanden in (nog zonder ze te kunnen lezen)? */
export function carriesFiles(transfer: Pick<DataTransfer, 'types'> | null | undefined): boolean {
    const types = Array.from(transfer?.types ?? []);

    return (
        types.includes('Files') ||
        types.some((type) => type.startsWith('image/') || type.startsWith('public.'))
    );
}
